import { QuestionCircleOutlined } from '@ant-design/icons';
import { trpc } from '@tencent/tpro_rmonitor_user_manager_user_role_auth';
import { Form, Button, Space, Select, Modal, message, Tooltip } from 'antd';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { Fragment, useState, useEffect, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BusiCode } from '@/infrastructure/axios/code';
import { WithTypeCast } from '@raft/utils';
import { AddApplyUserParams, applyCreate, AutoApplyParams, setExpireTimeStrategie } from './api';
import style from './index.module.scss';
import { RoleSelector } from './role-selector';
import {  RolesOption, User } from './types';
type IUser = WithTypeCast<trpc.rmonitor.user_manager.IUser>;
const { ExpireTimeStrategie  } = trpc.rmonitor.user_manager;
type IExpireTimeStrategie = WithTypeCast<trpc.rmonitor.user_manager.ExpireTimeStrategie>;

export enum ApplyMode {
  ALL = 0,
  INDIVIDUAL = 1,
  AUTO = 2,
}
interface CreateApplyProps {
  render: () => ReactNode;
  applyer?: string;
  role?: number;
  expireOptions: { label: string; value: number }[];
  userInfo?: User;
  rolesOption?: RolesOption[];
  adminList?: { label: string; value: string }[]
  operator?: string;
  userList?: IUser[];
  applyMode?: ApplyMode;
  selectedUsersLen?: number
  isAdmin?: boolean;
  getMemberList?: () => Promise<void>;
  selectedUsers?: IUser[]
}

const FormItem = Form.Item;

export function genCascacde(rawDepartments: string[]) {
  const departmentMap = new Map();
  // 处理每个路径，生成父子关系
  rawDepartments?.forEach((rawDepartment: string) => {
    const segments = rawDepartment.split('/');
    // 从完整路径开始，生成到上三级的所有路径
    for (let i = segments.length; i >= Math.max(Math.min(segments.length - 2, 3), 2); i--) {
      const currentRawDepartment = segments.slice(0, i).join('/');
      if (!departmentMap.has(currentRawDepartment)) {
        departmentMap.set(currentRawDepartment, new Set());
      }
      // 记录父子关系
      if (i < segments.length) {
        const parentPath = segments.slice(0, i).join('/');
        const childPath = segments.slice(0, i + 1).join('/');
        departmentMap.get(parentPath).add(childPath);
      }
    }
  });
  const departmentCascade: string[] = [];
  const baseDepartments = Array.from(departmentMap.keys())
    .filter((department) => {
      const segments = department.split('/');
      const pathLength = segments.length;
      return pathLength <= 3 ;
    })
    .sort();
  // 递归处理路径及其子路径
  function processBaseDepartment(baseDepartment: string) {
    departmentCascade.push(baseDepartment);
    const children = Array.from(departmentMap.get(baseDepartment) || []).sort();
    for (const child of children) {
      processBaseDepartment(child as string);
    }
  }

  // 处理每个基础路径
  baseDepartments.forEach((baseDepartment) => {
    processBaseDepartment(baseDepartment);
  });
  return [...new Set(departmentCascade)];
};

export const CreateApply = (props: CreateApplyProps) => {
  const { t } = useTranslation();
  const {
    render,
    applyer,
    expireOptions,
    role,
    userInfo,
    rolesOption,
    adminList,
    userList,
    applyMode,
    isAdmin,
    getMemberList,
    selectedUsers,
  } = props;
  const [visibleApply, setVisibleApply] = useState(false);
  const [form] = Form.useForm();
  const [expireOptionValid, setExpireOptionValid] =    useState<{ label: string; value: number }[]>();
  const [validity, setValidity] = useState<number>();
  const [selectedStrateies, setSelectedStrateies] = useState<IExpireTimeStrategie[]>([ExpireTimeStrategie.ALLUSER]);
  const [showDepartmentSelect, setShowDepartmentSelect] = useState<boolean>(false);
  const [selectedDepartment, setSelectedDepartment] = useState<string[]>([]);
  const [departmentOption, setDepartmentOption] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    const expireOptionValid = expireOptions.filter(expireOption => expireOption.value
      <= 360 - moment(userInfo?.expireTime).diff(moment(), 'days') - 1);
    setExpireOptionValid(expireOptionValid);
    setValidity(() => moment(userInfo?.expireTime).diff(moment(), 'days') + 1);
  }, [userInfo]);

  const desc = useMemo(() => {
    if (isEmpty(selectedUsers)) return t('「所有成员」');

    const selectedUserList = selectedUsers?.map(user => user.name as string) || [];
    const selectedUserStr = selectedUserList.slice(0, 3).join('、');

    return t('「{{names}}等{{count}} 位成员」', { names: selectedUserStr, count: selectedUsers?.length });
  }, [selectedUsers, t]);

  useEffect(() => {
    const departmentList = userList?.map(user => user.department);
    const cascadeDepartments = genCascacde(departmentList as string[]);
    setDepartmentOption(cascadeDepartments.map(department => ({
      label: department,
      value: department,
    })));
  }, [userList]);

  const defaultValue = adminList?.slice(0, 5).map(admin => admin.value);
  form.setFieldsValue({ name: defaultValue });
  const onApply = async (values: AddApplyUserParams) => {
    const body = {
      ...values,
      applyer,
      expireTime: applyMode === ApplyMode.INDIVIDUAL && values.expireTime ? values.expireTime! + moment(userInfo?.expireTime).diff(moment(), 'days') + 1 : values.expireTime,
    };
    const result = await applyCreate(body);
    if (result?.code === BusiCode.Ok) {
      message.success(t('申请成功'));
      getMemberList && await getMemberList();
    } else {
      message.error(t('申请失败'));
    }
  };

  const onSetExpireTimeStrategie = async (values: AutoApplyParams) => {
    const result = await setExpireTimeStrategie(values);
    if (result?.code === BusiCode.Ok) {
      message.success(t('申请成功'));
      getMemberList && await getMemberList();
    } else {
      message.error(t('申请失败'));
    }
  };
  const handleApply = () => {
    setVisibleApply(true);
  };

  const handleCloseApply = () => {
    setVisibleApply(false);
    form.resetFields();
  };

  const handleFinishApply =    validity === 360
    ? () => {
      setVisibleApply(false);
    }
    : async () => {
      try {
        await form.validateFields();
        onApply(form.getFieldsValue());
        handleCloseApply();
      } catch (error) {
        console.log(error);
      }
    };

  const handleBatchApply =  async () => {
    try {
      const userListWithRenewalTime = userList?.map(user => ({ ...user, renewalTime: moment(user.expireTime, 'X').diff(moment(), 'days') + 1 + form.getFieldValue('expireTime') }));
      onApply({ ...form.getFieldsValue(), userList: userListWithRenewalTime });
      handleCloseApply();
    } catch (error) {
      console.log(error);
    }
  };

  const autoApplyStrategie =  async () => {
    try {
      onSetExpireTimeStrategie({ strategies: selectedStrateies, departments: selectedDepartment });
      handleCloseApply();
    } catch (error) {
      console.log(error);
    }
  };

  const footerApply = validity === 360 ? (
      <div style={{ textAlign: 'right' }}>
        <Space>
          <Button onClick={handleFinishApply} type={'primary'}>
            {t('确定')}
          </Button>
        </Space>
      </div>
  ) : (
      <div style={{ textAlign: 'right' }}>
        <Space>
          <Button onClick={handleCloseApply}>{t('取消')}</Button>
          <Button onClick={handleFinishApply} type={'primary'}>
            {t('确定')}
          </Button>
        </Space>
      </div>
  );

  const footerBatchApply = (
    <div style={{ textAlign: 'right' }}>
      <Space>
        <Button onClick={handleCloseApply}>{t('取消')}</Button>
        <Button onClick={handleBatchApply} type={'primary'}>
          {t('确定')}
        </Button>
      </Space>
    </div>
  );

  const footerSetStrategie = (
  <div style={{ textAlign: 'right' }}>
    <Space>
      <Button onClick={handleCloseApply}>{t('取消')}</Button>
      <Button onClick={autoApplyStrategie} type={'primary'}>
        {t('确定')}
      </Button>
    </Space>
  </div>
  );

  const autoApplyStrateies = [
    { label: t('所有成员自动续期'), value: ExpireTimeStrategie.ALLUSER },
    { label: t('所有管理员自动续期'), value: ExpireTimeStrategie.ALLADMIN },
    { label: t('所有开发人员自动续期'), value: ExpireTimeStrategie.ALLDEVELOPER },
    { label: t('所有运营人员自动续期'), value: ExpireTimeStrategie.ALLOPERATOR },
    { label: t('指定组织架构人员自动续期'), value: ExpireTimeStrategie.Department },
  ];

  const handleApplyStrateieChange = (value: IExpireTimeStrategie[]) => {
    if (value[value.length - 1] === ExpireTimeStrategie.ALLUSER) {
      setSelectedStrateies([ExpireTimeStrategie.ALLUSER]);
    } else if (
      value.includes(ExpireTimeStrategie.ALLUSER)
      && value.length > 1
    ) {
      setSelectedStrateies([...new Set([ExpireTimeStrategie.ALLADMIN, ...value.slice(1)])]);
    } else if (!value.includes(ExpireTimeStrategie.ALLADMIN) && value.length !== 0) {
      setSelectedStrateies([ExpireTimeStrategie.ALLADMIN, ...value]);
    } else if (value.length === 0) {
      setSelectedStrateies([ExpireTimeStrategie.ALLUSER]);
    } else {
      setSelectedStrateies(value);
    }
    if (value.includes(ExpireTimeStrategie.Department)) {
      setShowDepartmentSelect(true);
    } else {
      setShowDepartmentSelect(false);
    }
  };

  const handleDepartmentChange = (value: string[]) => {
    setSelectedDepartment(value);
  };

  const renderApplyModal = () => {
    switch (applyMode) {
      case ApplyMode.ALL:
        return (
          <Fragment>
            <Modal
              title={t('一键续期确认')}
              visible={visibleApply}
              width={628}
              footer={footerBatchApply}
              onCancel={handleCloseApply}
              style={{ position: 'relative', top: '247px' }}
            >
              <div style={{ marginBottom: '24px' }}>
                {t('你确定要为{{desc}}执行批量续期操作吗？执行此操作将保留每位成员的当前角色。有效期如下，你可以调整。', { desc })}
              </div>
              <Form form={form} layout={'horizontal'} colon={false}>
                <FormItem
                  name={'expireTime'}
                  label={t('有效期')}
                  initialValue={
                    expireOptions
                    && Math.max(...expireOptions.map(item => item.value))
                  }
                  style={{ marginBottom: '16px' }}
                >
                  <Select
                    options={expireOptions}
                    placeholder={t('请选择有效期')}
                  />
                </FormItem>
              </Form>
            </Modal>
            <Space>
              <div>
                <span onClick={handleApply}>{render()}</span>
              </div>
            </Space>
          </Fragment>
        );
      case ApplyMode.AUTO:
        return (<Fragment>
          <Modal
            title={t('自动续期策略')}
            visible={visibleApply}
            width={628}
            footer={footerSetStrategie}
            onCancel={handleCloseApply}
            style={{ position: 'relative', top: '247px' }}
          >
            <div>
              <Form
                form={form}
                layout={'horizontal'}
                colon={false}
                labelCol={{ span: 4 }}
                wrapperCol={{ span: 20 }}
              >
                <div style={{ marginBottom: '24px' }}>
                <span>{t('自动续期模式：')}</span>
                <Select
                  mode="tags"
                  showSearch={true}
                  value={selectedStrateies}
                  options={autoApplyStrateies}
                  onChange={handleApplyStrateieChange}
                  allowClear={true}
                  showArrow
                  style={{ width: '450px' }}
                />
                </div>
                {showDepartmentSelect && <div><span>{t('指定组织架构：')}</span>
                <Select
                  mode="tags"
                  showSearch={true}
                  value={selectedDepartment}
                  options={departmentOption}
                  onChange={handleDepartmentChange}
                  allowClear={true}
                  showArrow
                  style={{ width: '450px' }}
                  className={style.autoApplySelect}
                /></div>}
              </Form>
            </div>
          </Modal>
          <Space>
            <div>
              <span onClick={handleApply}>{render()}</span>
            </div>
          </Space>
        </Fragment>);
      default:
        return (validity === 360 ? (
          <Fragment>
            <Modal visible={visibleApply} width={628} footer={footerApply} onCancel={() => setVisibleApply(false)}>
              <p>{t('{{applyer}}距离到期日期还有360天，已经是最大的有效期，无需申请续期。', { applyer })}</p>
            </Modal>
            <Space>
              <div>
                <span onClick={handleApply}>{render()}</span>
              </div>
            </Space>
          </Fragment>
        ) : (
          <Fragment>
            <Modal
              title={t('续期确认')}
              visible={visibleApply}
              width={628}
              footer={footerApply}
              onCancel={handleCloseApply}
              style={{ position: 'relative', top: '247px' }}
            >
              <div style={{ marginBottom: '16px' }}>
                {t('确认要给{{applyer}}执行续期操作吗？默认按以下角色和有效期进行续期，你可以调整。', { applyer })}
              </div>
              <div>
                <Form
                  form={form}
                  layout={'horizontal'}
                  colon={false}
                  labelCol={{ span: 4 }}
                  wrapperCol={{ span: 20 }}
                >
                  <FormItem initialValue={role} name={'roleId'} label={t('角色')} rules={[{ required: true }]}>
                    <RoleSelector
                      key={'add_user'}
                      rolesOption={rolesOption!}
                      placeholder={t('请选择角色')}
                    />
                  </FormItem>
                  <FormItem
                    name={'expireTime'}
                    label={t('有效期')}
                    initialValue={expireOptionValid && Math.max(...expireOptionValid.map(item => item.value))}
                    help={
                      <span style={{ color: 'red' }}>
                        {t('有效期从续期申请日开始算起。')}
                      </span>
                    }
                    style={{ marginBottom: '16px' }}
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={expireOptionValid}
                      placeholder={t('请选择有效期')}
                    />
                  </FormItem>
                  {!isAdmin && <FormItem
                    name={'name'}
                    label={
                      <>
                        {t('通知审批人')}
                        <Tooltip
                          title={t('最多可以选择5个管理员进行通知，Bugly平台将通过「Bugly通知机器人」进行通知。')}
                        >
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </>
                    }
                    rules={[{ required: true }]}
                    className={style.approverSelect}
                  >
                    <Select
                      mode="multiple"
                      options={adminList}
                      placeholder={t('请选择审批人')}
                      allowClear
                    />
                  </FormItem>}
                </Form>
              </div>
            </Modal>
            <Space>
              <div>
                <span onClick={handleApply}>{render()}</span>
              </div>
            </Space>
          </Fragment>
        ));
    }
  };

  return <>{renderApplyModal()}</>;
};
